/**
 * Product 2025 Cart Monitor
 * Handles automatic removal of items with matching Order_ID when any item is manually removed
 * 
 * Improved Logic:
 * 1. When any item is manually removed, check if it has an Order_ID
 * 2. If it does, check the cart again for the same Order_ID and automatically remove that too
 * 3. These Order_ID usually come in pairs, one custom name service item and a product
 * 4. If it doesn't have the same Order_ID then skip and do not auto delete
 * 5. Be mindful that not all items have order_id
 */

// Cart monitor script file loaded

try {
  (function() {
    'use strict';

    // Cart monitor script loaded successfully

    var isMonitoring = false;
    var retryCount = 0;
    var maxRetries = 3;
    var eventListenersAdded = false;

    /**
     * Get current cart state from Shopify API
     * @returns {Promise<Object|null>} Cart data or null if error
     */
    function getCartState() {
      return fetch('/cart.js', {
        method: 'GET',
        headers: { 'Accept': 'application/json' }
      })
      .then(function(response) {
        if (!response.ok) throw new Error('Cart fetch failed');
        return response.json();
      })
      .catch(function(error) {
        return null;
      });
    }

    /**
     * Remove item from cart by key
     * @param {string} key - Cart item key
     * @returns {Promise<Object|null>} Response data or null if error
     */
    function removeItemByKey(key) {
      return fetch('/cart/change.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          id: key,
          quantity: 0
        })
      })
      .then(function(response) {
        if (!response.ok) throw new Error('Remove item failed');
        return response.json();
      })
      .catch(function(error) {
        return null;
      });
    }

    /**
     * Refresh cart drawer UI to show updated cart state
     */
    function refreshCartDrawerUI() {
      // Prevent multiple simultaneous refresh attempts
      if (window.cartRefreshInProgress) {
        return;
      }
      
      window.cartRefreshInProgress = true;
      
      // Clear the flag after 2 seconds to prevent it from getting stuck
      setTimeout(function() {
        window.cartRefreshInProgress = false;
      }, 2000);
      
      // Get fresh cart state first
      getCartState().then(function(cart) {
        if (!cart) {
          window.cartRefreshInProgress = false;
          return;
        }
        
        // Dispatch cart:updated event with actual cart data
        document.dispatchEvent(new CustomEvent('cart:updated', {
          detail: {
            cart: cart
          }
        }));
        
        // Dispatch cart:build event to trigger cart drawer rebuild
        document.dispatchEvent(new CustomEvent('cart:build'));
        
        // Update cart totals directly if elements exist (backup)
        if (cart.total_price) {
          var subtotalElement = document.querySelector('[data-subtotal], .cart-subtotal, .cart__subtotal');
          if (subtotalElement && typeof theme !== 'undefined' && theme.Currency) {
            subtotalElement.innerHTML = theme.Currency.formatMoney(cart.total_price, theme.settings.moneyFormat);
          }
          
          var itemCountElement = document.querySelector('[data-cart-count], .cart-count, .cart__count');
          if (itemCountElement) {
            itemCountElement.textContent = cart.item_count;
          }
        }
        
        window.cartRefreshInProgress = false;
        
      }).catch(function(error) {
        window.cartRefreshInProgress = false;
      });
    }

    /**
     * Extract _Order_ID from cart API by item key
     * @param {string} itemKey - Cart item key
     * @returns {Promise<string|null>} _Order_ID or null if not found
     */
    function extractOrderIdByKey(itemKey) {
      return getCartState().then(function(cart) {
        if (!cart || !cart.items) return null;
        
        var item = cart.items.find(function(i) {
          return i.key === itemKey;
        });
        
        if (item && item.properties && item.properties['_Order_ID']) {
          return item.properties['_Order_ID'];
        }
        
        return null;
      }).catch(function() {
        return null;
      });
    }

    /**
     * Remove items with the same Order_ID when any item is removed
     * Improved logic: Only remove if there are matching Order_IDs, be mindful that not all items have Order_ID
     * @param {string} removedOrderId - Order_ID of removed item
     */
    function removeRelatedItems(removedOrderId) {
      if (!removedOrderId) {
        return;
      }

      // Prevent multiple simultaneous removals for the same Order_ID
      if (window.pendingOrderIdRemoval && window.pendingOrderIdRemoval === removedOrderId) {
        return;
      }

      // Set flag to prevent multiple simultaneous removals
      window.pendingOrderIdRemoval = removedOrderId;

      // Clear the flag after 10 seconds to prevent it from getting stuck
      setTimeout(function() {
        if (window.pendingOrderIdRemoval === removedOrderId) {
          window.pendingOrderIdRemoval = null;
        }
      }, 10000);

      getCartState().then(function(cart) {
        if (!cart || !cart.items) {
          window.pendingOrderIdRemoval = null;
          return;
        }

        // Find items with matching _Order_ID (excluding the one already removed)
        var relatedItems = cart.items.filter(function(item) {
          var itemOrderId = item.properties && item.properties['_Order_ID'];
          return itemOrderId === removedOrderId;
        });

        if (relatedItems.length > 0) {
          // Remove all related items
          var removePromises = relatedItems.map(function(item) {
            return removeItemByKey(item.key);
          });

          Promise.all(removePromises).then(function() {
            refreshCartDrawerUI();
          }).catch(function(error) {
            // Silent error handling
          }).finally(function() {
            window.pendingOrderIdRemoval = null;
          });
        } else {
          window.pendingOrderIdRemoval = null;
        }
      }).catch(function(error) {
        window.pendingOrderIdRemoval = null;
      });
    }

    /**
     * Handle remove button click - remove item and any related items with same Order_ID
     * @param {Event} event - Click event
     */
    function handleRemoveClick(event) {
      // Prevent duplicate processing of the same click event
      if (event.processedByCartMonitor) {
        return;
      }
      
      // Mark event as processed immediately to prevent any race conditions
      event.processedByCartMonitor = true;

      // CRITICAL: Prevent ALL default behavior since we're handling removal ourselves
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();

      // Get the cart item this button belongs to
      var cartItem = event.target.closest('.cart__item, .cart-item, [data-cart-item], .cart-item-wrapper, .cart-item__details, .cart-item__content, .cart-item__info');
      if (!cartItem) {
        return;
      }

      // Get the item key from the cart item
      var itemKey = cartItem.getAttribute('data-key');
      if (!itemKey) {
        return;
      }

      // Extract _Order_ID from the cart API before removing
      extractOrderIdByKey(itemKey).then(function(orderId) {
        // Remove the item using our own logic
        removeItemByKey(itemKey).then(function(response) {
          if (response) {
            // Remove the DOM element immediately for better UX
            cartItem.remove();
            
            // Update cart totals
            if (response.total_price) {
              var subtotalElement = document.querySelector('[data-subtotal], .cart-subtotal, .cart__subtotal');
              if (subtotalElement && typeof theme !== 'undefined' && theme.Currency) {
                subtotalElement.innerHTML = theme.Currency.formatMoney(response.total_price, theme.settings.moneyFormat);
              }
              
              var itemCountElement = document.querySelector('[data-cart-count], .cart-count, .cart__count');
              if (itemCountElement) {
                itemCountElement.textContent = response.item_count;
              }
            }
            
            // Dispatch cart:updated event
            document.dispatchEvent(new CustomEvent('cart:updated', {
              detail: {
                cart: response
              }
            }));
            
            // If this item has an _Order_ID, check for and remove related items
            if (orderId) {
              setTimeout(function() {
                removeRelatedItems(orderId);
              }, 500); // Delay to allow main item removal to complete
            }
          }
        }).catch(function(error) {
          // Silent error handling
        });
      });
    }

    /**
     * Override onclick attributes on remove buttons to prevent default behavior
     */
    function overrideRemoveButtonBehavior() {
      var removeButtons = document.querySelectorAll('.ajaxcart__remove, .cart__remove, .remove-item, [data-remove-item], button[data-cart-remove]');
      
      removeButtons.forEach(function(button) {
        if (!button.hasAttribute('data-product2025-overridden')) {
          // Remove the original onclick to prevent it from executing
          if (button.hasAttribute('onclick')) {
            button.removeAttribute('onclick');
          }
          
          // Mark this button as overridden to avoid processing it again
          button.setAttribute('data-product2025-overridden', 'true');
          
          // Add our own click handler directly to the button with capture phase
          button.addEventListener('click', function(event) {
            handleRemoveClick(event);
          }, true); // Use capture phase to ensure our handler runs first
          
          // Also add a handler in bubble phase as backup
          button.addEventListener('click', function(event) {
            handleRemoveClick(event);
          }, false);
        }
      });
    }

    /**
     * Setup cart monitoring using event delegation
     */
    function setupCartMonitoring() {
      if (isMonitoring && eventListenersAdded) {
        return;
      }

      // Find cart drawer or cart container - try multiple selectors
      var cartContainer = document.querySelector(
        '#CartDrawer, .drawer--right, .cart-drawer, [data-cart-drawer], .cart-drawer__contents, .cart__contents, .drawer__contents, .cart, .cart-drawer__wrapper, [class*="cart"], [class*="drawer"]'
      );

      if (!cartContainer) {
        retryCount++;
        if (retryCount < maxRetries) {
          setTimeout(setupCartMonitoring, 1000);
        }
        return;
      }

      // Override onclick behavior on existing remove buttons
      overrideRemoveButtonBehavior();

      // Only add event listeners once
      if (!eventListenersAdded) {
        // Use document-level event delegation for reliable remove button detection
        // This catches any dynamically added buttons that weren't overridden
        document.addEventListener('click', function(event) {
          // Check if the clicked element is a remove button using more specific selectors
          if (event.target.matches('a[href*="/cart/change"], button[data-cart-remove], .cart__remove, .remove-item, [data-remove-item], .ajaxcart__remove')) {
            
            // Only process if this button hasn't been directly overridden
            if (!event.target.hasAttribute('data-product2025-overridden')) {
              handleRemoveClick(event);
            }
          }
        });

        eventListenersAdded = true;
      }

      isMonitoring = true;
    }

    /**
     * Initialize the cart monitor
     */
    function initializeCartMonitor() {
      if (window.__pedalNamePairingBound) return;
      // Try to setup monitoring immediately
      setupCartMonitoring();

      // Listen for cart events to ensure monitoring is active
      document.addEventListener('cart:open', function() {
        retryCount = 0;
        setTimeout(function() {
          setupCartMonitoring();
          overrideRemoveButtonBehavior(); // Override any new buttons
        }, 500);
      });

      document.addEventListener('cart:updated', function() {
        retryCount = 0;
        setTimeout(function() {
          setupCartMonitoring();
          overrideRemoveButtonBehavior(); // Override any new buttons
        }, 500);
      });

      // Watch for cart drawer visibility changes
      var cartDrawer = document.querySelector('.drawer--right');
      if (cartDrawer) {
        var observer = new MutationObserver(function(mutations) {
          mutations.forEach(function(mutation) {
            if (mutation.type === 'attributes' && mutation.attributeName === 'class') {
              var target = mutation.target;
              if (!target.classList.contains('is-empty') && target.classList.contains('drawer--is-open')) {
                retryCount = 0;
                setTimeout(function() {
                  setupCartMonitoring();
                  overrideRemoveButtonBehavior(); // Override any new buttons
                }, 500);
              }
            }
          });
        });

        observer.observe(cartDrawer, {
          attributes: true,
          attributeFilter: ['class']
        });
      }
    }

    // Initialize when DOM is ready
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', initializeCartMonitor);
    } else {
      initializeCartMonitor();
    }

    // Expose functions for debugging
    window.Product2025CartMonitor = {
      getCartState: getCartState,
      removeItemByKey: removeItemByKey,
      extractOrderIdFromElement: extractOrderIdFromElement,
      removeRelatedItems: removeRelatedItems,
      setupCartMonitoring: setupCartMonitoring,
      refreshCartDrawerUI: refreshCartDrawerUI,
      overrideRemoveButtonBehavior: overrideRemoveButtonBehavior
    };

  })();
} catch (error) {
  // Silent error handling
}
